/**
 * Wave 1A — Results real-data registry.
 * Presentation offers never carry rawDeal; selection resolves raw via offerId.
 */

import { mapFlightOfferViewModel } from "../view-models";
import { BAGGAGE_STATUS } from "../view-models/types";
import { resolveAgentFlightDisplayedSortPrice } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveAgentFlightDisplayedSortPrice";
import { toGreenfieldResultsOffer } from "./toGreenfieldResultsOffer";

/**
 * @param {import("../view-models/types").BaggageViewModel[]|null|undefined} baggage
 * @returns {string[]}
 */
export function mapResultsBaggageChipsFromVm(baggage) {
  const rows = Array.isArray(baggage) ? baggage : [];
  if (
    rows.length === 0 ||
    rows.every((r) => r?.status === BAGGAGE_STATUS.UNKNOWN)
  ) {
    return ["פרטי כבודה בפירוט"];
  }

  const chips = [];
  for (const b of rows) {
    if (!b?.status || b.status === BAGGAGE_STATUS.UNKNOWN) continue;
    if (b.status === BAGGAGE_STATUS.INCLUDED) {
      if (b.label && /טרולי/u.test(b.label) && !/מזוודה/u.test(b.label)) {
        chips.push("טרולי");
        continue;
      }
      if (b.label && /מזוודה/u.test(b.label) && !/טרולי/u.test(b.label)) {
        chips.push("מזוודה");
        continue;
      }
      if (b.kind === "carryOn") chips.push("טרולי");
      else if (b.kind === "checked") {
        const q = Number(b.quantity);
        chips.push(q > 1 ? `${q} מזוודות` : "מזוודה");
      } else if (b.label) {
        // Compact multi-line included labels from V1 helper
        if (/טרולי/u.test(b.label)) chips.push("טרולי");
        if (/מזוודה/u.test(b.label)) chips.push("מזוודה");
        if (!/טרולי|מזוודה/u.test(b.label)) chips.push("כבודה כלולה");
      } else chips.push("כבודה כלולה");
      continue;
    }
    if (b.status === BAGGAGE_STATUS.AVAILABLE_FOR_PURCHASE) {
      chips.push("כבודה בתשלום");
      continue;
    }
    if (b.status === BAGGAGE_STATUS.SELECTED_PAID) {
      chips.push("כבודה נבחרה");
      continue;
    }
    if (b.status === BAGGAGE_STATUS.NOT_INCLUDED) {
      chips.push("ללא כבודה");
      continue;
    }
    if (b.status === BAGGAGE_STATUS.UNAVAILABLE) {
      chips.push("כבודה לא זמינה");
    }
  }

  if (chips.length === 0) return ["פרטי כבודה בפירוט"];
  const unique = [...new Set(chips)];
  if (unique.length === 1 && unique[0] === "טרולי") return ["טרולי בלבד"];
  return unique;
}

/**
 * Strip provider raw payload for presentation components.
 * @param {object} rawOffer
 * @param {ReturnType<typeof mapFlightOfferViewModel>} vm
 */
export function toResultsPresentationOffer(rawOffer, vm) {
  if (!rawOffer || typeof rawOffer !== "object") return null;
  const { rawDeal: _rawDeal, ...safe } = rawOffer;
  return {
    ...safe,
    fe2BaggageChips: mapResultsBaggageChipsFromVm(vm?.baggage),
    fe2PriceDisplay: vm?.price?.display ?? null,
    fe2ViewModelId: vm?.id ?? String(rawOffer.id),
  };
}

/**
 * Stable distinguisher for offers that share a surrogate id.
 * Flight numbers, times, and customer price — not list position.
 * @param {object} raw
 */
function offerSelectionDisambiguator(raw) {
  const legs = Array.isArray(raw?.cardLegRows) ? raw.cardLegRows : [];
  const legPart = legs
    .map((leg) =>
      [
        leg?.flightNumber || "",
        leg?.departureTime || "",
        leg?.arrivalTime || "",
        leg?.originCode || "",
        leg?.destinationCode || "",
      ].join("~")
    )
    .join(">");
  const slicePart = ["outbound", "inbound"]
    .map((dir) => {
      const slice = raw?.[dir];
      if (!slice || typeof slice !== "object") return "";
      const segs = Array.isArray(slice.segments) ? slice.segments : [];
      const numbers = segs.map((s) => s?.flightNumber || "").join(",");
      return [
        numbers,
        slice.departureAt || "",
        slice.arrivalAt || "",
        slice.origin || "",
        slice.destination || "",
      ].join("~");
    })
    .join(">");
  const price = [
    raw?.priceClientPerPersonUsd ?? "",
    raw?.priceClientPerPersonIls ?? "",
    raw?.providerPriceAmount ?? "",
    raw?.pricePerPerson ?? "",
    raw?.price?.amount ?? "",
  ].join("~");
  return [
    raw?.provider || "",
    raw?.providerOfferId || "",
    raw?.itineraryIdentity || "",
    legPart,
    slicePart,
    price,
  ].join("||");
}

/**
 * @param {object[]} list
 * @returns {Set<string>}
 */
function duplicateBaseOfferIds(list) {
  /** @type {Map<string, number>} */
  const counts = new Map();
  for (const raw of list) {
    if (!raw || raw.id == null) continue;
    const base = String(raw.id);
    counts.set(base, (counts.get(base) || 0) + 1);
  }
  return new Set(
    [...counts.entries()].filter(([, n]) => n > 1).map(([id]) => id)
  );
}

/**
 * Unique registry key. Shared surrogate ids are split by itinerary + price.
 * @param {object} raw
 * @param {Set<string>} duplicateBaseIds
 */
function selectionRegistryKey(raw, duplicateBaseIds) {
  const base = String(raw.id);
  if (!duplicateBaseIds.has(base)) return base;
  return `sel::${base}::${offerSelectionDisambiguator(raw)}`;
}

/**
 * @param {object[]} rawOffers — same sorted/filtered list V1 uses
 * @param {{
 *   cheapestIds?: Set<string>|null,
 *   airlineNameMap?: Map<string,string>|Record<string,string>|null,
 * }} [opts]
 */
export function buildResultsWave1aRegistry(rawOffers, opts = {}) {
  const list = Array.isArray(rawOffers) ? rawOffers : [];
  /** @type {Map<string, object>} */
  const byId = new Map();
  const viewModels = [];
  const presentationOffers = [];
  const greenfieldOffers = [];
  const cheapestIds = opts.cheapestIds || null;
  const duplicateBaseIds = duplicateBaseOfferIds(list);

  for (const raw of list) {
    if (!raw || raw.id == null) continue;
    const baseId = String(raw.id);
    const id = selectionRegistryKey(raw, duplicateBaseIds);
    // Keep the original object when its id is already unique.
    // A shared surrogate id gets a shallow copy whose id is the selection key,
    // so the clicked card, registry, selectedFlightOffer, and Details share one id.
    const record = id === baseId ? raw : { ...raw, id };
    byId.set(id, record);
    const vm = mapFlightOfferViewModel(record);
    if (!vm) continue;
    viewModels.push(vm);
    const presentation = toResultsPresentationOffer(record, vm);
    if (presentation) presentationOffers.push(presentation);
    const greenfield = toGreenfieldResultsOffer(vm, record, {
      isCheapest: cheapestIds ? cheapestIds.has(baseId) : false,
      airlineNameMap: opts.airlineNameMap ?? null,
    });
    if (greenfield) greenfieldOffers.push(greenfield);
  }

  return { byId, viewModels, presentationOffers, greenfieldOffers };
}

/**
 * @param {{ byId: Map<string, object> }} registry
 * @param {string} offerId
 */
export function resolveRawOfferFromRegistry(registry, offerId) {
  if (!registry?.byId || offerId == null) return null;
  return registry.byId.get(String(offerId)) ?? null;
}

/**
 * Dev-only parity dump — never call in production UI paths without DEV guard.
 * @param {object[]} rawOffers
 * @param {ReturnType<typeof buildResultsWave1aRegistry>} registry
 */
export function logResultsWave1aParity(rawOffers, registry) {
  const isDev =
    (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
    (typeof process !== "undefined" && process.env?.NODE_ENV === "development");
  if (!isDev) return;

  const raw = Array.isArray(rawOffers) ? rawOffers : [];
  const vms = registry?.viewModels || [];
  const rows = raw.map((o, i) => {
    const vm = vms[i] || vms.find((v) => String(v.id) === String(o?.id));
    const v1Price = resolveAgentFlightDisplayedSortPrice(o);
    const v2Price = vm?.price?.display || null;
    return {
      id: o?.id,
      vmId: vm?.id ?? null,
      v1SortPrice: v1Price?.sortPrice ?? null,
      v2SortPrice: v2Price?.sortPrice ?? null,
      v1CurrencyPrimaryIls: v1Price?.isPrimaryCurrencyIls ?? null,
      v2CurrencyPrimaryIls: v2Price?.isPrimaryCurrencyIls ?? null,
      baggage: (vm?.baggage || []).map((b) => b.status),
    };
  });

  // eslint-disable-next-line no-console
  console.info("[FE2_RESULTS_WAVE1A_PARITY]", {
    v1Count: raw.length,
    v2Count: vms.length,
    idsEqual:
      raw.map((o) => String(o.id)).join("|") ===
      vms.map((v) => String(v.id)).join("|"),
    sample: rows.slice(0, 12),
    totalRows: rows.length,
  });
}

/**
 * Assert presentation never exposes rawDeal (tests / DEV).
 * @param {object} presentationOffer
 */
export function presentationOfferHasRawDeal(presentationOffer) {
  return Boolean(
    presentationOffer &&
      Object.prototype.hasOwnProperty.call(presentationOffer, "rawDeal") &&
      presentationOffer.rawDeal != null
  );
}
